// src/components/MeuPainel.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
// Importa apenas o que é necessário: doc, updateDoc e onSnapshot (para usersprivado)
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import '../styles/Colaborador.css';

// Componentes de widget
import HistoricoSalarial from './Colaborador/HistoricoSalarial';
import TermometroHumor from './Colaborador/TermometroHumor';
import PlanoDesenvolvimentoIndividual from './Colaborador/PlanoDesenvolvimentoIndividual';
import AreaENPS from './Colaborador/AreaENPS';
import SistemaFeedback from './Colaborador/SistemaFeedback';
import ControlePonto from './Colaborador/ControlePonto';

// Biblioteca Drag-and-Drop e um novo ícone
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical } from 'react-icons/fa';

const SortableWidget = ({ widgetId, title, className, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: widgetId });
  
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
  
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        <section className={`colaborador-section ${className} card`}>
          <div className="card-header-draggable">
            <h3>{title}</h3>
            <button className="drag-handle" {...listeners} aria-label="Arrastar para reordenar">
              <FaGripVertical />
            </button>
          </div>
          <div className="card-body-content">
            {children}
          </div>
        </section>
      </div>
    );
};

// Configuração dos widgets e seus módulos necessários
const WIDGET_CONFIG = {
    ponto: { title: 'Controle de Ponto', className: 'controle-ponto-section', requiredModule: 'CONTROLE_PONTO' },
    enps: { title: 'Termômetro da Felicidade', className: 'enps-section', requiredModule: 'ENPS' },
    humor: { title: 'Termômetro de Humor Diário', className: 'humor-section', requiredModule: 'HUMOR' },
    pdi: { title: 'Plano de Desenvolvimento Individual (PDI)', className: 'pdi-section', requiredModule: 'PDI' },
    feedback: { title: 'Sistema de Feedback', className: 'feedback-sistema-section', requiredModule: 'FEEDBACK' },
    salario: { title: 'Histórico de Recebimento', className: 'historico-salarial-section', requiredModule: 'SALARIO' },
};

// O componente agora recebe 'userData' do Dashboard.js
const MeuPainel = ({ userData }) => { 
  const [widgetOrder, setWidgetOrder] = useState([]);
  const [privateData, setPrivateData] = useState(null); // Estado para dados de 'usersprivado'
  
  // Este useEffect reage a mudanças no 'userData' (vindo do Dashboard)
  // e define a ordem dos widgets com base nos módulos assinados.
  useEffect(() => {
    if (userData?.id) {
      const subscribedModules = userData.modulosAssinados || [];
      const fullDefaultOrder = ['ponto', 'enps', 'humor', 'pdi', 'feedback', 'salario'];

      // Verifica se o cliente tem o módulo 'ACESSO_TOTAL'
      const hasTotalAccess = subscribedModules.includes('ACESSO_TOTAL');

      const accessibleWidgets = fullDefaultOrder.filter(widgetId => {
          const config = WIDGET_CONFIG[widgetId];
          // Um widget é acessível se:
          // 1. O cliente tem acesso total.
          // 2. O widget não requer um módulo.
          // 3. O cliente tem o módulo específico para aquele widget.
          return hasTotalAccess || !config.requiredModule || subscribedModules.includes(config.requiredModule);
      });
      
      // Usa a ordem salva no perfil do usuário ou a ordem padrão filtrada
      const savedOrder = userData.dashboardWidgetOrder || accessibleWidgets;
      // Garante que apenas widgets acessíveis sejam exibidos
      const finalOrder = savedOrder.filter(widgetId => accessibleWidgets.includes(widgetId));
      
      setWidgetOrder(finalOrder);
    }
  }, [userData]); // Depende do userData

  // Este useEffect busca os dados privados (como PDI, salário) em tempo real
  // assim que o ID do usuário estiver disponível.
  useEffect(() => {
      if (!userData?.id) return; // Espera o userData ter um id
      
      const userPrivateDocRef = doc(db, 'usersprivado', userData.id);
      
      // Usa onSnapshot para manter PDI, humor, salário, etc., atualizados
      const unsubscribePrivate = onSnapshot(userPrivateDocRef, (docSnap) => {
          if (docSnap.exists()) {
              setPrivateData(docSnap.data());
          } else {
              setPrivateData({}); // Define como objeto vazio se não existir
          }
      }, (err) => {
          console.error("Falha ao ouvir dados privados:", err);
          // Opcional: setar um estado de erro para dados privados
      });

      // Limpa o listener quando o componente/usuário mudar
      return () => unsubscribePrivate();
  }, [userData?.id]); // Depende apenas do ID do usuário

  // Função para salvar a nova ordem dos widgets no banco
  const saveWidgetOrder = async (orderIds) => {
    if (!userData?.id) return;
    try {
      const userDocRef = doc(db, 'users', userData.id);
      await updateDoc(userDocRef, { dashboardWidgetOrder: orderIds });
    } catch (error) {
      console.error("Erro ao salvar a ordem dos widgets:", error);
    }
  };

  // Handler para o fim do 'arrastar e soltar'
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setWidgetOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        saveWidgetOrder(newOrder); // Salva a nova ordem
        return newOrder;
      });
    }
  };

  // Função que renderiza o componente de widget correto
  const renderWidgetById = (widgetId) => {
    // Dados privados são lidos do estado 'privateData'
    const pdiData = privateData?.pdi || { objetivoPrincipal: '', metas: [], feedbackContinua: [] };
    const historicoSalarial = privateData?.["historicoSalarial "] || privateData?.historicoSalarial || [];
    const termometroHumor = privateData?.termometroHumor || [];
    
    // Dados públicos (ID, nome, email, etc.) vêm do 'userData'
    switch(widgetId) {
        case 'ponto':
            return <ControlePonto userId={userData?.id} empresaId={userData?.empresa_id} />;
        case 'enps':
            return <AreaENPS departamentoId={userData?.departamentoId} empresaId={userData?.empresa_id} />;
        case 'humor':
            return <TermometroHumor 
                        historicoHumor={termometroHumor} 
                        userId={userData?.id} 
                        userData={userData} // Passa o userData completo
                        empresaId={userData?.empresa_id}
                    />;
        case 'pdi':
            return <PlanoDesenvolvimentoIndividual pdiData={pdiData} userId={userData?.id} userName={userData?.name} onPDIUpdate={() => {}} />;
        case 'feedback':
            // Passa um objeto unificado para o componente de Feedback
            return <SistemaFeedback currentUser={{
                uid: userData?.id,
                displayName: userData?.name,
                email: userData?.email,
                empresa_id: userData?.empresa_id,
                departamento: userData?.departamento,
                role: userData?.role // 'role' agora vem do userData
            }} />;
        case 'salario':
            return <HistoricoSalarial historico={historicoSalarial} />;
        default:
            return <div className='error-message'>Widget não encontrado</div>;
    }
  };

  // Se o Dashboard.js ainda não passou o userData, exibe um carregamento
  if (!userData) {
    return <div className="loading-spinner">Carregando dados...</div>;
  }

  // Renderização principal do componente
  return (
    <div className="colaborador-wrapper">
      <div className="colaborador-header">
        {/* Usa os dados do 'userData' para o cabeçalho */}
        <h2>Painel do Colaborador: {userData?.name}</h2>
        <p>Departamento: {userData?.departamento || 'Não informado'}</p>
        <p className="text-muted small" style={{marginTop: '8px'}}>Use o ícone <FaGripVertical style={{verticalAlign: 'middle'}}/> para arrastar e reordenar seu painel.</p>
      </div>

      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={widgetOrder}
          strategy={verticalListSortingStrategy}
        >
          <div className="colaborador-grid">
            {widgetOrder.map(widgetId => (
              <SortableWidget
                key={widgetId}
                widgetId={widgetId}
                title={WIDGET_CONFIG[widgetId]?.title}
                className={WIDGET_CONFIG[widgetId]?.className}
              >
                {/* Verifica se os dados privados já carregaram.
                  Se 'privateData' for null, mostra um carregamento.
                  Se for um objeto (mesmo vazio {}), renderiza o widget.
                */}
                {privateData ? renderWidgetById(widgetId) : <p>Carregando widget...</p>}
              </SortableWidget>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default MeuPainel;